import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { CookieConsent } from './CookieConsent';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/event', label: 'The Event' },
  { to: '/photos', label: 'Photos' },
  { to: '/guestbook', label: 'Guestbook' },
  { to: '/rsvp', label: 'RSVP' },
  { to: '/donate', label: 'Donate' },
  { to: '/contact', label: 'Contact' },
];

const navClass = ({ isActive }: { isActive: boolean }) =>
  `font-nav text-[14px] font-medium uppercase tracking-wide transition-colors ${
    isActive ? 'text-ink-50 border-b-2 border-pink-500 pb-0.5' : 'text-ink-200 hover:text-pink-300'
  }`;

/** Edem-style wordmark: pink bracket, heavy uppercase text, pink dot. */
export function Logo({ className = '' }: { className?: string }) {
  return (
    <Link
      to="/"
      className={`relative inline-flex items-center pl-3 ${className}`}
      aria-label="BrandonStrong.com home"
    >
      <span className="absolute left-0 top-0 h-full w-[3px] bg-pink-500" aria-hidden />
      <span className="absolute left-0 top-0 h-[3px] w-5 bg-pink-500" aria-hidden />
      <span className="font-display text-xl font-black uppercase leading-none tracking-tight text-ink-50 sm:text-2xl">
        Brandon<span className="text-pink-500">Strong</span>
      </span>
    </Link>
  );
}

/** True once the page has scrolled past the top, so the fixed header can pick up a background. */
function useScrolled(threshold = 16): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

/**
 * Small-screen menu: a button in the header opens a full-screen sheet with the
 * same links and the reserve button. It closes on navigation, on Escape, and
 * on the backdrop; the page behind stops scrolling while it is open.
 */
function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const location = useLocation();
  useEffect(() => {
    onClose();
    // Close whenever the route changes; onClose is stable from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      id="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      className="fixed inset-0 z-40 flex flex-col overscroll-contain bg-night-950/95 backdrop-blur lg:hidden"
    >
      <div className="flex items-center justify-between px-4 py-5">
        <Logo />
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 w-11 items-center justify-center text-ink-50"
          aria-label="Close menu"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" />
          </svg>
        </button>
      </div>
      {/* The overlay locks the page; the list itself scrolls so every item and the
          buttons are reachable on a short phone. */}
      <nav
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-6 pb-[max(2rem,env(safe-area-inset-bottom))] pt-4"
        aria-label="Primary"
      >
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `border-b border-white/10 py-4 font-display text-3xl font-black uppercase [@media(max-height:740px)]:py-3 [@media(max-height:740px)]:text-2xl ${
                isActive ? 'text-pink-500' : 'text-ink-50'
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
        <Link to="/rooms" className="btn-primary mt-8 text-center">
          Reserve a room
        </Link>
      </nav>
    </div>
  );
}

export function PublicLayout() {
  const scrolled = useScrolled();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex min-h-screen flex-col">
      {/* Sticky on scroll: transparent over the hero, a slim dark bar once the page moves. */}
      <header
        className={`fixed inset-x-0 top-0 z-30 transition-[background-color,box-shadow] duration-200 ${
          scrolled
            ? 'border-b border-white/10 bg-night-950/95 shadow-lg shadow-black/30 backdrop-blur'
            : ''
        }`}
      >
        <div
          className={`mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 transition-[padding] duration-200 ${scrolled ? 'py-3' : 'py-5 sm:py-6'}`}
        >
          <Logo />
          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={navClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          {/* Phones get the logo and the menu button only: the reserve button is in
              the menu and the hero, and three items do not fit a 360px header. */}
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to="/rooms"
              className="btn-primary hidden shrink-0 whitespace-nowrap !px-5 !py-2.5 text-[13px] sm:inline-flex lg:!px-6 lg:!py-3 lg:text-[14px]"
            >
              Reserve a room
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-11 w-11 shrink-0 items-center justify-center text-ink-50 lg:hidden"
              aria-label="Open menu"
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2.2" />
              </svg>
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      <main className="flex-1">
        <Suspense fallback={<div className="py-32 text-center text-ink-400">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t border-white/10 bg-night-900">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 px-4 py-10 md:flex-row">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
            <Logo />
            <div className="text-[13px] text-ink-600">
              <p>
                <span className="text-pink-500">BrandonStrong.com</span> &copy;{' '}
                {new Date().getFullYear()}. All rights reserved.
              </p>
              <p className="mt-1">
                Room payments and donations are processed by MobiusPay for Brandon&apos;s family
                and appear on your card statement as{' '}
                <span className="text-ink-400">Icandy Enterprises</span>.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-5 text-[13px] uppercase text-ink-400">
            <Link to="/contact" className="whitespace-nowrap hover:text-pink-300">
              Contact
            </Link>
            <Link to="/terms" className="whitespace-nowrap hover:text-pink-300">
              Terms &amp; Policy
            </Link>
            <Link to="/privacy" className="whitespace-nowrap hover:text-pink-300">
              Privacy
            </Link>
          </div>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}
